import axios from 'axios'
import { camelKeys } from 'js-convert-case'
import { getResponseErrorMessage } from '@/store/utils'
import formsModule from './forms'
import { paginationState, paginationMutations } from '@/store/mixins/pagination'

const state = {
  userGroups: [],
  ...paginationState,
}

const getState = () => JSON.parse(JSON.stringify(state))

export default {
  namespaced: true,
  modules: {
    forms: formsModule,
  },
  state: getState(),
  getters: {
    userGroups (state) {
      return state.userGroups.map(userGroup => ({
        ...userGroup,
        membersCount: userGroup.users ? userGroup.users.length : 0,
        envelopesCount: userGroup.envelopes ? userGroup.envelopes.length : 0,
      }))
    },
  },
  actions: {
    initialize ({ dispatch }) {
      return dispatch('fetchUserGroups')
    },
    setQuery ({ commit, dispatch }, query) {
      commit('SET_QUERY', query)
      return dispatch('fetchUserGroups')
    },
    fetchUserGroups ({ commit, state, rootGetters }) {
      if (!rootGetters.accountId) {
        throw new Error('Account id has to be set in the state')
      }

      commit('SET_IS_LOADING', true, { root: true })
      let url = `/user_groups?by_account_id=${rootGetters.accountId}&expand=1&page=${state.page}&limit=${state.perPage}`
      if (state.query) {
        url += `&by_name=${state.query}`
      }

      return axios
        .get(url)
        .then((response) => {
          commit('SET_USER_GROUPS', response.data)
          commit('SET_NUMBER_OF_PAGES', response.headers['total-pages'])
          commit('SET_TOTAL_RESULTS', response.headers['total-count'])
        })
        .catch(error => {
          console.error(error)
          const errorMessage = getResponseErrorMessage(error) ?? 'Napaka pri pridobivanju skupin uporabnikov, prosimo poskusite kasneje.'
          commit('SET_MESSAGE', { text: errorMessage, type: 'error' }, { root: true })
        })
        .finally(() => commit('SET_IS_LOADING', false, { root: true }))
    },
    goToPage ({ commit, dispatch, state }, page) {
      if (page > 0 && page <= state.numberOfPages) {
        commit('SET_PAGE', page)
        return dispatch('fetchUserGroups')
      }
    },
    addMemberToGroup ({ commit }, { userGroupId, userId }) {
      commit('SET_IS_LOADING', true, { root: true })
      return axios
        .post(`/user_groups/${userGroupId}/user_group_memberships`, {
          user_group_membership: { user_id: userId },
        })
        .then(() => {
          commit('SET_MESSAGE', { text: 'Član je bil uspešno dodan v skupino.', type: 'success' }, { root: true })
        })
        .catch(error => {
          console.error(error)
          const errorMessage = getResponseErrorMessage(error) ?? 'Napaka pri dodajanju člana v skupino, prosimo poskusite kasneje.'
          commit('SET_MESSAGE', { text: errorMessage, type: 'error' }, { root: true })
          throw error
        })
        .finally(() => commit('SET_IS_LOADING', false, { root: true }))
    },
    removeMemberFromGroup ({ commit }, { userGroupId, userId }) {
      commit('SET_IS_LOADING', true, { root: true })
      return axios
        .get(`/user_groups/${userGroupId}/user_group_memberships`)
        .then((membershipsResponse) => {
          const membership = membershipsResponse.data.find(m => m.user_id === userId)
          if (membership) {
            return axios.delete(`/user_group_memberships/${membership.id}`)
          }
        })
        .then(() => {
          commit('SET_MESSAGE', { text: 'Član je bil uspešno odstranjen iz skupine.', type: 'success' }, { root: true })
        })
        .catch(error => {
          console.error(error)
          const errorMessage = getResponseErrorMessage(error) ?? 'Napaka pri odstranjevanju člana iz skupine, prosimo poskusite kasneje.'
          commit('SET_MESSAGE', { text: errorMessage, type: 'error' }, { root: true })
          throw error
        })
        .finally(() => commit('SET_IS_LOADING', false, { root: true }))
    },
    deleteUserGroup ({ commit }, id) {
      commit('SET_IS_LOADING', true, { root: true })
      return axios
        .delete(`/user_groups/${id}`)
        .then(() => {
          commit('SET_MESSAGE', { text: 'Skupina uporabnikov je bila uspešno izbrisana.', type: 'success' }, { root: true })
        })
        .catch(error => {
          console.error(error)
          const errorMessage = getResponseErrorMessage(error) ?? 'Napaka pri brisanju skupine uporabnikov, prosimo poskusite kasneje.'
          commit('SET_MESSAGE', { text: errorMessage, type: 'error' }, { root: true })
          throw error
        })
        .finally(() => commit('SET_IS_LOADING', false, { root: true }))
    },
    reset ({ commit }) {
      commit('RESET_STATE')
    },
  },
  mutations: {
    SET_USER_GROUPS (state, userGroups) {
      state.userGroups = userGroups.map(group => {
        return {
          ...camelKeys(group, { recursive: true, recursiveInArray: true }),
          membersCount: group.users ? group.users.length : 0,
        }
      })
    },
    RESET_STATE (state) {
      Object.assign(state, getState())
    },
    ...paginationMutations,
  },
}
